import { z } from "zod";

// ============================================================================
// CONNECT INSTAGRAM (Direct Meta — Instagram Login, single endpoint)
// ============================================================================

/**
 * Connect Instagram Body Schema
 *
 * After the Instagram Login authorize step, the frontend sends the one-time
 * authorization code plus the exact `redirectUri` it authorized with. The backend
 * exchanges the code for tokens server-side (short → long, never to the browser),
 * reads the account, subscribes it to the messages webhook, and creates the
 * standalone META_INSTAGRAM connection.
 *
 * `redirectUri` MUST byte-match the value used at the authorize step — Instagram
 * rejects the code exchange otherwise. (This is the opposite of WhatsApp Embedded
 * Signup, whose code originates from a JS SDK popup and carries no redirect_uri.)
 */
export const ConnectInstagramBodySchema = z.object({
  /** One-time authorization code from the Instagram Login redirect */
  code: z.string().min(1),
  /** Redirect URI used at the authorize step; must byte-match it exactly */
  redirectUri: z.string().min(1)
});

export type ConnectInstagramBody = z.infer<typeof ConnectInstagramBodySchema>;

/**
 * Connect Instagram Response Schema
 */
export const ConnectInstagramResponseSchema = z.object({
  /** Created (or refreshed) ApiKeyConnection id */
  connectionId: z.string().uuid(),
  /** The connected Instagram account id (webhook `entry.id` / routing anchor) */
  instagramAccountId: z.string(),
  /** The connected account's @handle (display) */
  username: z.string()
});

export type ConnectInstagramResponse = z.infer<typeof ConnectInstagramResponseSchema>;
